/**
 * 引擎 ↔ Vue 的**唯一桥**（PLAN §8.1）。
 *
 * 边界纪律：
 *  · 引擎实例与 <svg> 一律 markRaw + shallowRef，绝不进深度响应式
 *  · 文档变更只靠 version 自增通知 UI，UI 再按需 query（绝不监听 DOM 反推）
 *  · 组件不直接改 DOM，只调 execute(op)；**唯一的例外是拖动预览**（render/transform-session.ts）
 */
import { computed, ref, shallowRef, type ComputedRef, type Ref, type ShallowRef } from 'vue';
import type { Rgba } from '@/core/color';
import { EMPTY_DOC } from '@/demo';
import { isEditable, pathOf, walkEditable } from '@/core/dom';
import { IDENTITY, type Mat, type Pt, type Rect } from '@/core/geom/matrix';
import {
  bboxInRoot,
  bboxOfMany,
  centerView,
  clampZoom,
  fitView,
  matrixToRoot,
  screenToRoot,
  viewBoxRect,
  type ViewState,
} from '@/core/geom/viewport';
import { History, type Command } from '@/core/history';
import { attrPatch, type Patch } from '@/core/patch';
import type { Header } from '@/core/io/header';
import { parseSvgText } from '@/core/io/parse';
import { downloadFile, svgBlob, svgTextForExport } from '@/core/ops/file';
import { MAX_RASTER_PX, rasterFileName, rasterizeSvg, type RasterFormat } from '@/core/io/raster';
import { buildPdf } from '@/core/io/pdf-browser';
import type { PdfPage } from '@/core/io/pdf';
import {
  alignCommand,
  distributeCommand,
  type AlignMode,
  type DistributeMode,
} from '@/core/ops/align';
import {
  deleteElements,
  duplicateElements,
  groupElements,
  reorderElements,
  ungroupElements,
  type OrderMode,
  type StructureResult,
} from '@/core/ops/structure';
import { nudgeBy, type TransformDeps } from '@/core/ops/transform';
import { clipboard } from '@/core/clipboard';
import { createDraftStore, createIdbStorage, createMemoryStorage, type DraftStore } from '@/core/draft';
import { isEmbedSession } from '@/embed';
import {
  buildProject,
  parseProject,
  pathOfElement,
  projectFilename,
  projectToText,
  resolvePaths,
  ProjectError,
} from '@/core/io/project';
import {
  canvasBackgroundOf,
  readCanvas,
  setCanvasBackground,
  setCanvasRatio,
  setCanvasSize,
  type CanvasSize,
  type CanvasSnapshot,
} from '@/core/ops/canvas';
import { canMoveElement, collectLockedPaths, isLocked, renameId, restoreLockedState, setLocked } from '@/core/ops/layer';
import { isConvertibleTag } from '@/core/geom/local-bbox';
import {
  applyColorMapping as applyColorMappingCmd,
  clearSpotlight,
  collectColorUsage,
  markElements,
  paintOrigins,
  recolorElements,
  resolveOne,
  resolveMapping,
  type ColorUsage,
  type PaintOrigin,
} from '@/core/palette';
import { buildGroups, type ElementGroup } from '@/core/query/group';
import { replaceShapes } from '@/core/ops/shape';
import { SHAPE_LABEL, type ShapeKind } from '@/core/shapes';
import { loadPaletteState, palette, resetPaletteDocState } from '@/stores/palette';
import { markShapeApplied, resetShapeDocState, setShapeHint, shapeState } from '@/stores/shapes';
import { looksLikeSvgDocument, sourceReplaceCommand } from '@/core/ops/source';
import { firstTextOf, setHidden, setTextAttrs, setTextLines, setVertical, type TextAttrChanges } from '@/core/ops/text';
import { readTextProps, textOwner, isTextLike, type TextProps } from '@/core/query/text';
import { createTextEdit, type TextEditHandle } from '@/render/text-edit';
import { setCornerRadius, setGeometry, setRotation, type GeometryTarget } from '@/core/ops/geometry';
import { buildList, filterList, type ListModel } from '@/core/query/list';
import { readProps, type PropsSnapshot } from '@/core/query/props';
import { attachPointerInteraction, resolveSelectable, type SelectMode } from '@/render/interactions/pointer';
import { attachDrawInteraction } from '@/render/interactions/draw';
import { attachTransformInteraction } from '@/render/interactions/transform';
import { createMount, type MountHandle } from '@/render/mount';
import { createOverlay, frameFromRects, type Frame } from '@/render/overlay';
import { gridSize, IDLE_STATUS, isDrawTool, POSITION_LOCKED_STATUS, ui } from '@/stores/editor';

/** 屏幕 → 用户坐标的映射（默认走真实 getScreenCTM；测试注入恒等映射，从而不依赖真实布局） */
type Mapper = (svg: SVGSVGElement, p: Pt) => Pt | null;
let mapper: Mapper | null = null;
export const setCoordinateMapper = (next: Mapper | null): void => {
  mapper = next;
};

export interface RasterExportOptions {
  format: RasterFormat;
  /** 倍率模式 */
  scale?: number;
  /** dpi 模式（需同时给物理宽度 mm） */
  dpi?: number;
  mmWidth?: number;
  /** null = 透明（JPEG 忽略并铺白） */
  background?: string | null;
  /** JPEG 质量 0–1 */
  quality?: number;
}

export interface PdfExportOptions {
  /** 页面规格：按图幅 / A4 竖 / A4 横（页面盒换算见 core/io/pdf 的 pdfPageBox） */
  page?: PdfPage;
}

export interface EditorApi {
  svg: ShallowRef<SVGSVGElement | null>;
  version: Ref<number>;
  selection: ShallowRef<Element[]>;
  pristine: ComputedRef<boolean>;
  canUndo: ComputedRef<boolean>;
  canRedo: ComputedRef<boolean>;
  /** 可撤销步数（命令条数） */
  undoDepth: ComputedRef<number>;
  list: ComputedRef<ListModel>;
  /** 有文档但一个元素都没有 —— 空画板（引导卡与拖拽落区据此出现） */
  blank: ComputedRef<boolean>;
  filteredGroups: ComputedRef<ListModel['groups']>;
  props: ComputedRef<PropsSnapshot | null>;
  selectionLabel: ComputedRef<string>;
  mount(host: HTMLElement): void;
  unmount(): void;
  loadText(text: string): boolean;
  openFile(file: File): Promise<boolean>;
  execute(cmd: { label: string; patches: import('@/core/patch').Patch[]; mergeKey?: string }): boolean;
  undo(): void;
  redo(): void;
  select(els: Element[], mode?: SelectMode): void;
  clearSelection(): void;
  exportText(): string;
  /** 当前文档名（导出文件名用；未打开文件时为 null） */
  sourceName: Ref<string | null>;
  download(filename?: string): void;
  /** 导出位图（PNG/JPEG）：尺寸、背景、质量见 core/io/raster；错误以 ui.status 呈现 */
  exportRaster(options: RasterExportOptions): Promise<{ ok: boolean; message: string }>;
  /** 矢量 PDF（jsPDF + svg2pdf.js，两个库都动态加载） */
  exportPdf(options?: PdfExportOptions): Promise<{ ok: boolean; message: string }>;
  setZoom(zoom: number): void;
  panBy(dx: number, dy: number): void;
  zoomFit(): void;
  zoomToSelection(): void;
  syncOverlay(): void;
  /** 容器尺寸变化（拖分栏/窗口）后：重排 + 未手动平移过就保持居中 */
  relayout(): void;
  // ---- M1：结构 / 对齐 / 微移 ----
  deleteSelection(): boolean;
  selectAll(): void;
  groupSelection(): boolean;
  ungroupSelection(): boolean;
  duplicateSelection(): boolean;
  reorderSelection(mode: OrderMode): boolean;
  alignSelection(mode: AlignMode): boolean;
  distributeSelection(mode: DistributeMode): boolean;
  nudgeSelection(dx: number, dy: number): boolean;
  exitGroupContext(): boolean;
  // ---- M1：数值输入与剪贴板 ----
  setSelectionGeometry(target: GeometryTarget): boolean;
  setSelectionRotation(deg: number): boolean;
  setSelectionRadius(radius: number): boolean;
  copySelection(): number;
  cutSelection(): boolean;
  pasteClipboard(): boolean;
  canPaste: ComputedRef<boolean>;
  // ---- M2：文本 ----
  textProps: ComputedRef<TextProps | null>;
  textEditing: ShallowRef<Element | null>;
  beginTextEdit(): boolean;
  /** 改选中文字的内容；`mergeKey` 用于面板里边打边写（整段合并成一条 undo） */
  setTextContent(value: string, opts?: { mergeKey?: string }): boolean;
  setTextStyle(changes: TextAttrChanges): boolean;
  toggleVertical(): boolean;
  setHidden(hidden: boolean, els?: Element[]): boolean;
  toggleHidden(els?: Element[]): boolean;
  // ---- M3：持久化、源码、画布、锁定、草稿 ----
  sourceText: ComputedRef<string>;
  applySource(text: string): boolean;
  newDocument(): void;
  openText(text: string, name?: string | null): boolean;
  exportProject(): void;
  importProjectText(text: string): boolean;
  openProjectFile(file: File): Promise<boolean>;
  canvasInfo: ComputedRef<CanvasSnapshot | null>;
  docBackground: ComputedRef<string | null>;
  setCanvasSize(size: CanvasSize): boolean;
  setCanvasRatio(ratio: number | null): boolean;
  setCanvasBackground(color: string | null, mode: 'view' | 'doc'): boolean;
  lockSelection(locked: boolean): boolean;
  lockElement(el: Element, locked: boolean): boolean;
  /**
   * **位置锁**（默认开，装载文档时重置为开）：文档原有图元的位置/尺寸/旋转一律改不动，
   * 但**仍可点击选中**、改颜色/文字。本次会话新建的元素不受限。
   */
  geometryLocked: ComputedRef<boolean>;
  /** 选区**整体**能不能动（右栏几何输入框 / 对齐分布按钮的禁用判据） */
  canMoveSelection: ComputedRef<boolean>;
  setGeometryLocked(locked: boolean): void;
  renameSelectionId(id: string): boolean;
  renameElementId(el: Element, id: string): boolean;
  colorUsages: ComputedRef<ColorUsage[]>;
  /** 悬停色板行：预览（虚线框住命中元素 + 其余压暗），**不改选中集**；null 收起 */
  previewColor(hex: string | null): void;
  // ---- 形状工作台（按颜色 / 形状 / 大小分组后统一形状）----
  /** 按当前轴与范围分组（只读派生；轴与范围的开关在 stores/shapes） */
  shapeGroups: ComputedRef<ElementGroup[]>;
  /** 悬停某一行：预览（虚线框住这一组的元素 + 其余压暗），**不改选中集**；null 收起 */
  previewShapeGroup(key: string | null): void;
  /** 点行：把这一组设为选中集（之后可以直接用右栏属性 / Delete） */
  selectShapeGroup(key: string): boolean;
  /**
   * 把这一组换成同一种形状。**一组 = 一条命令 = 一次 Ctrl+Z**。
   * 位置锁开着也允许（它只保中心与包围盒，不改位置）—— 否则正是这个功能要服务的散点图会被拦住。
   */
  applyShapeGroup(key: string, kind: ShapeKind): boolean;
  /**
   * 把**当前选中集**换成同一种形状（属性面板「形状」区用；一个元素只是这批长度为 1 的特例）。
   * 口径与 `applyShapeGroup` 完全一致：一批 = 一条命令、位置锁不拦、换完选中集跟到新节点上。
   */
  applySelectionShape(kind: ShapeKind): boolean;
  applyColorMapping(opts?: { selectionOnly?: boolean; keepLuminance?: boolean }): boolean;
  /**
   * ★ 选一个颜色**立刻生效**（一行一次、可 Ctrl+Z 逐步撤回；连续调色传 merge 合并成一条）。
   * 已写过一次的行按**元素**改色（源色此时可能已不在文档里了）。
   */
  pickColorMapping(source: string, target: string, opts?: { merge?: boolean }): boolean;
  /** 把某一行改过的颜色**按原字面量**还回去（"清除这一行"用）；返回是否真还原了 */
  revertColorPick(source: string): boolean;
  draft: ShallowRef<{ savedAt: number; name: string | null } | null>;
  restoreDraft(): Promise<boolean>;
  discardDraft(): Promise<void>;
  flushDraft(): Promise<void>;
}

const svg = shallowRef<SVGSVGElement | null>(null);
/** 剪贴板块数（剪贴板本身不是响应式的，用它驱动 UI 可用性） */
const clipboardCount = ref(0);
const version = ref(0);
const selection = shallowRef<Element[]>([]);
const sourceName = ref<string | null>(null);

let handle: MountHandle | null = null;
let overlay: ReturnType<typeof createOverlay> | null = null;
let detachPointer: (() => void) | null = null;
let detachTransform: (() => void) | null = null;
let detachDraw: (() => void) | null = null;
let history: History | null = null;
let textEdit: TextEditHandle | null = null;
/** 用户是否手动平移过视图（true → 容器尺寸变化时不强行居中） */
let viewPinned = false;
let draftStore: DraftStore | null = null;
const draftInfo = shallowRef<{ savedAt: number; name: string | null } | null>(null);
let detachDraft: (() => void) | null = null;
const textEditing = shallowRef<Element | null>(null);
let header: Header = { leadingComments: [] };
let rawSource: string | null = null;

/* ------------------------------------------------------------------ 位置锁
 * 「本次会话新建的元素」集合：绘制 / 粘贴 / 原地复制产生的那批（`ext-*`）。
 * 位置锁开着时**只有它们**可以移动 —— 文件里原有的图元一律冻结。
 *
 * 为什么用内存里的 WeakSet 而不是文档属性：
 *  · 不进文档 → 导出零字节影响、不需要"导出时剥除"、不碰工程文件与草稿；
 *  · 撤销 / 重做不会换元素身份（结构 patch 走真实 appendChild），标记天然跟着走；
 *  · 装载新文档时元素全是新的 → 直接换一个空集合，不必清理。
 */
let freshElements = new WeakSet<Element>();

/** 标记（连同可编辑后代一起 —— 组里有一个不是新建的，整个组就不算新建） */
function markFresh(el: Element): void {
  freshElements.add(el);
  for (const child of Array.from(el.children)) if (isEditable(child)) markFresh(child);
}

const isFresh = (el: Element): boolean => freshElements.has(el);

/**
 * 元素**整体**能不能动：它自己和全部可编辑后代都得是「本次新建」。
 * 组里混着一个原有图元就不行 —— 一动就把实验图元带着走了（这正是要防的）。
 */
function allFresh(el: Element): boolean {
  if (!isFresh(el)) return false;
  for (const child of Array.from(el.children)) {
    if (isEditable(child) && !allFresh(child)) return false;
  }
  return true;
}

/** 位置锁判据（唯一入口，交互层与 UI 都走它） */
const canMove = (el: Element): boolean =>
  canMoveElement(el, { positionLocked: ui.geometryLocked, isFresh: allFresh });

/** 选区**整体**能不能动：有一个不行就整体不行（手柄框按整集算，只动一半会让锚点错位） */
const selectionMovable = (): boolean => {
  const els = selection.value;
  return els.length > 0 && els.every(canMove);
};

/** 被位置锁拦下：写状态栏（提示的唯一出口）并返回 false —— 不产生命令、不进历史 */
function refuseMove(): boolean {
  ui.status = POSITION_LOCKED_STATUS;
  return false;
}

/**
 * 文档状态变化的**唯一通知口**。
 *
 * 除了（让 Vue 重新渲染的）版本号，还必须重排承载 svg 的容器：
 * 画板尺寸由 viewBox 决定，改画布尺寸 / 换文档后若不重排，
 * **画板在屏幕上还是旧尺寸**（内容缩放了也看不出来，用户会以为"改了没反应"）。
 */
const bump = (): void => {
  version.value += 1;
  handle?.relayout();
  if (!viewPinned) centerAt(ui.zoom); // 尺寸变了就按新尺寸重新居中
  applyHighlight();
};

/** 悬停预览的颜色（临时态，**不动选中集**） */
let previewHex: string | null = null;
/** 形状工作台的悬停预览元素（临时态，同样不动选中集；与 previewHex 二选一） */
let previewShapeEls: Element[] | null = null;

/**
 * 当前悬停要预览的元素 —— 色板（按颜色）与形状工作台（按组）共用这一个出口，
 * 于是"框选预览 + 其余压暗"的规则只有一份实现。
 */
function highlightTargets(): Element[] {
  const root = svg.value;
  if (!root) return [];
  if (previewHex) {
    return (api.colorUsages.value.find((u) => u.hex === previewHex)?.elements ?? []).filter((el) => el.isConnected);
  }
  if (previewShapeEls) {
    return previewShapeEls.filter((el) => el.isConnected && root.contains(el));
  }
  return [];
}

/**
 * 色板"选色立刻生效"的记账：源色 → { 被改写的元素, 最后写入的颜色 }。
 * 有它才能在**同一行重选**时按元素改造（源色这时已经不在文档里了），
 * 以及"清除这一行"时把颜色改回源色。
 */
interface PickRecord {
  els: Element[];
  rgba: Rgba;
  /** 最后写入的颜色 */
  hex: string;
  /** 改写前的原字面量（"清除这一行"要**原样**还回去，连 #FFF/white/style 写法都不变） */
  origins: PaintOrigin[];
}
const picked = new Map<string, PickRecord>();

/** 把之前改过的颜色按原字面量还回去（一条可撤销命令）；null = 没得还 */
function revertPickCommand(rec: PickRecord): Command | null {
  const root = svg.value;
  if (!root) return null;
  const patches: Patch[] = [];
  for (const o of rec.origins) {
    const path = pathOf(o.el, root);
    if (!path?.length) continue;
    const current = o.el.getAttribute(o.attr);
    if (current === o.value) continue;
    const p = attrPatch(path, o.attr, current, o.value);
    if (p) patches.push(p);
  }
  return patches.length > 0 ? { label: '还原颜色', patches } : null;
}

/**
 * "高亮"的统一应用口（**完全由悬停驱动**）。
 * 只做两件事：写 `data-editor-dim`（导出必被剥）＋ 覆盖层画虚线预览框。
 * 文档变了（bump）也会走这里重算 —— 命中集可能变，颜色被改没了就自动收起。
 */
function applyHighlight(): void {
  const root = svg.value;
  if (!root) return;
  clearSpotlight(root);
  const preview = highlightTargets();
  const marked = preview.length > 0 ? markElements(root, preview) : 0;
  handle?.host.classList.toggle('is-spotlight', marked > 0);
  if (marked === 0) {
    // 命中没了（颜色被改掉了 / 那一组刚被换走）→ 收起悬停态，别留一个空高亮
    previewHex = null;
    previewShapeEls = null;
  }
  syncOverlay();
}

const pristine = computed(() => {
  void version.value;
  return (history?.length ?? 0) === 0;
});
const canUndo = computed(() => {
  void version.value;
  return history?.canUndo ?? false;
});
const canRedo = computed(() => {
  void version.value;
  return history?.canRedo ?? false;
});
const undoDepth = computed(() => {
  void version.value;
  return history?.length ?? 0;
});
const list = computed<ListModel>(() => {
  void version.value;
  const root = svg.value;
  return root ? buildList(root) : { items: [], groups: [], total: 0 };
});
const filteredGroups = computed(() => filterList(list.value.groups, ui.search));
/** 空画板：有文档、但一个元素都没有。引导卡与拖拽落区的唯一判据（三处 UI 共用） */
const blank = computed(() => !!svg.value && list.value.total === 0);
const props = computed<PropsSnapshot | null>(() => {
  void version.value;
  void selection.value;
  if (!svg.value) return null;
  return selection.value.length > 0 ? readProps(selection.value) : null;
});
const selectionLabel = computed(() => {
  const p = props.value;
  if (!p || p.count === 0) return IDLE_STATUS;
  if (p.count > 1) return `已选 ${p.count} 个元素`;
  return `${p.tag} · ${p.id ? `#${p.id}` : '（无 id）'}`;
});

/** 元素「父坐标系 → 根坐标」的矩阵 */
const parentMatOf = (el: Element): Mat => {
  const root = svg.value;
  if (!root) return IDENTITY;
  return matrixToRoot(el.parentElement ?? root, root) ?? IDENTITY;
};

const toRoot = (clientX: number, clientY: number): Pt | null => {
  const root = svg.value;
  if (!root) return null;
  return (mapper ?? screenToRoot)(root, { x: clientX, y: clientY });
};

const boxesOwned = (els: readonly Element[]): Rect[] => {
  const root = svg.value;
  if (!root) return [];
  return els
    .map((el) => bboxInRoot(el as SVGGraphicsElement, root))
    .filter((b): b is Rect => b !== null);
};

const selectionBox = (): Rect | null => {
  const root = svg.value;
  if (!root) return null;
  return bboxOfMany(selection.value, root);
};

const transformDeps = (): TransformDeps | null => {
  const root = svg.value;
  return root ? { root, parentMatOf } : null;
};

const frames = (): Frame[] => {
  const host = handle?.host;
  if (!host) return [];
  const hostRect = host.getBoundingClientRect();
  return selection.value.map((el) => frameFromRects(el.getBoundingClientRect(), hostRect));
};

function syncOverlay(): void {
  // 手柄只在「选中单个、且位置可改」时出现：位置锁开着时不给手柄（不摆假控件 —— 拖了没反应最糟）
  const only = selection.value.length === 1 ? selection.value[0] : null;
  overlay?.setFrames(frames(), { showHandles: only !== null && canMove(only) });
  overlay?.setPreview(previewFrames());
}

/** 悬停预览框（只画框、不改选中集；缩放/平移后会跟着重画） */
function previewFrames(): Frame[] | null {
  const host = handle?.host;
  if (!host) return null;
  const hit = highlightTargets();
  if (hit.length === 0) return null;
  const hostRect = host.getBoundingClientRect();
  return hit.map((el) => frameFromRects(el.getBoundingClientRect(), hostRect));
}

function setSelection(els: Element[], mode: SelectMode = 'replace'): void {
  const current = selection.value;
  let next: Element[];
  switch (mode) {
    case 'add':
      next = [...current.filter((el) => !els.includes(el)), ...els];
      break;
    case 'toggle': {
      const first = els[0];
      next = first ? (current.includes(first) ? current.filter((el) => el !== first) : [...current, ...els]) : current;
      break;
    }
    default:
      next = [...els];
  }
  selection.value = next; // 替换引用（不原地 mutate）
  ui.status = selectionLabel.value;
  syncOverlay();
}

/**
 * 换形状的**身份交接**（交给 core/ops/shape 的 `onSwap`）。
 *
 * 换形状是**重建节点**（不是改属性），于是两份按节点身份记的状态会当场失效，必须搬到新节点上：
 *  · 「本次新建」标记 —— 否则刚画出来的图形换一下形状就变成「文档原有图元」，位置锁立刻拦住它；
 *  · 选中集 —— 否则属性面板与画布选框拿的还是已游离的旧节点，换完形状当场变成「未选中元素」。
 * 撤销 / 重做走的是同一个回调，所以 Ctrl+Z 回来这两条也还在。
 */
function shapeSwapHandoff(oldEl: Element, newEl: Element): void {
  if (allFresh(oldEl)) markFresh(newEl);
  const i = selection.value.indexOf(oldEl);
  if (i < 0) return;
  const next = [...selection.value];
  next[i] = newEl;
  setSelection(next, 'replace');
}

const applyView = (next: Partial<ViewState>): void => {
  ui.zoom = clampZoom(next.zoom ?? ui.zoom);
  ui.panX = next.panX ?? ui.panX;
  ui.panY = next.panY ?? ui.panY;
  handle?.setView({ zoom: ui.zoom, panX: ui.panX, panY: ui.panY });
  syncOverlay();
};

function panBy(dx: number, dy: number): void {
  viewPinned = true; // 用户手动平移过 → 之后不再自动居中
  applyView({ panX: ui.panX + dx, panY: ui.panY + dy });
}

/** 把画板按给定缩放居中（容器尺寸变化、打开文档、Fit 都走这里） */
function centerAt(zoom: number): void {
  const root = svg.value;
  const host = handle?.host;
  if (!root || !host) return;
  const content = viewBoxRect(root);
  if (!content) return;
  const size = { width: host.clientWidth, height: host.clientHeight };
  if (size.width === 0 || size.height === 0) {
    // 首帧还没量到尺寸 → 下一帧补一次（只补一次，避免死循环）
    requestAnimationFrame(() => {
      const next = handle?.host;
      if (next && next.clientWidth > 0) {
        applyView(centerView({ width: next.clientWidth, height: next.clientHeight }, content, zoom));
      }
    });
    return;
  }
  applyView(centerView(size, content, zoom));
}

/** 视图默认态：**100% + 画板居中**（对齐参考产品；「Fit」按钮才是铺满窗口） */
function resetView(): void {
  viewPinned = false;
  centerAt(1); // 100% + 居中
}

function zoomFit(): void {
  const root = svg.value;
  const host = handle?.host;
  if (!root || !host) return;
  const content =
    bboxOfMany(
      list.value.items.map((i) => i.element),
      root,
    ) ?? viewBoxRect(root);
  if (!content) return;
  applyView(fitView({ width: host.clientWidth, height: host.clientHeight }, content, 24));
}

/** 结构类 op 的统一收尾：执行 + 采纳建议选中集 */
function applyStructure(res: StructureResult): boolean {
  const ok = api.execute(res.cmd);
  if (ok) setSelection(res.selection, 'replace');
  return ok;
}

/** 文档变更后排程一次草稿落盘（节流在 DraftStore 内部；pristine 不写） */
function scheduleDraft(): void {
  if (!draftStore || pristine.value) return;
  draftStore.schedule(() => ({
    svg: api.exportText(),
    name: sourceName.value,
    view: { zoom: ui.zoom, panX: ui.panX, panY: ui.panY },
  }));
}

const api: EditorApi = {
  svg,
  sourceName,
  version,
  selection,
  pristine,
  canUndo,
  canRedo,
  undoDepth,
  list,
  blank,
  filteredGroups,
  props,
  selectionLabel,

  mount(host: HTMLElement) {
    if (handle) return;
    handle = createMount(host, { zoom: ui.zoom, panX: ui.panX, panY: ui.panY });
    overlay = createOverlay(host);
    textEdit = createTextEdit({
      host,
      execute: (cmd) => api.execute(cmd),
      syncOverlay,
      rectOf: (el) => el.getBoundingClientRect(),
      zoom: () => ui.zoom,
      onEditingChange: (el) => {
        textEditing.value = el;
      },
    });
    detachPointer = attachPointerInteraction({
      host,
      getSvg: () => svg.value,
      getSelection: () => selection.value,
      setSelection,
      panBy,
      isPanMode: () => ui.tool === 'hand',
      setMarquee: (frame) => overlay?.setMarquee(frame),
      onEmptyClick: () => setSelection([], 'replace'),
      getContext: () => ui.groupContext,
      setContext: (ctx) => {
        ui.groupContext = ctx;
      },
      isDrawing: () => isDrawTool(ui.tool),
      isSelectable: (el: Element) => !isLocked(el), // 锁定 → 画布上点不动（仍可从列表选中）
      beginTextEdit: (el) => {
        const target = textOwner(el) ?? el;
        return isTextLike(target) ? (textEdit?.begin(target) ?? false) : false;
      },
    });
    detachTransform = attachTransformInteraction({
      host,
      getSvg: () => svg.value,
      getSelection: () => selection.value,
      getBox: selectionBox,
      setSelection: (els) => setSelection(els, 'replace'),
      toRoot,
      parentMatOf,
      execute: (cmd) => api.execute(cmd),
      syncOverlay,
      grid: gridSize,
      resolveTarget: (target) => {
        const root = svg.value;
        if (!root) return null;
        const hit = resolveSelectable(target, root, ui.groupContext, (el) => !isLocked(el));
        return hit;
      },
      isDrawing: () => isDrawTool(ui.tool),
      /** 位置锁：文档原有图元不可动（手柄、拖动都在这里被拦下） */
      canTransform: (el: Element) => canMove(el),
      onRefuse: () => {
        ui.status = POSITION_LOCKED_STATUS;
      },
    });
    detachDraw = attachDrawInteraction({
      host,
      getSvg: () => svg.value,
      toRoot,
      tool: () => ui.tool,
      setTool: (tool) => {
        ui.tool = tool;
      },
      parent: () => ui.groupContext ?? svg.value,
      execute: (cmd) => api.execute(cmd),
      select: (el) => setSelection([el], 'replace'),
      afterCreate: (el, tag) => {
        markFresh(el); // 本次会话新建 → 位置锁不拦它（新画的批注要能摆位）
        // 文本工具：落地即进入就地编辑，省一次双击
        if (tag === 'text') textEdit?.begin(el);
      },
      syncOverlay,
      style: () => ({}),
    });
    if (svg.value) handle.svg = svg.value;
    resetView(); // 打开即 100% 居中（不是铺满）

    loadPaletteState(); // 品牌色/开关从 localStorage 恢复

    // 自动草稿：IndexedDB 不可用时降级到内存（至少本会话内有效）。
    // ⚠️ 嵌入模式（宿主 iframe + ?embed=1）下**不建草稿**：宿主是同一个弹窗轮流编辑不同文件，
    //    而草稿是按文档存的 → 编辑 A 没保存就关、再打开 B 时提示"恢复"会把 A 的内容塞进 B。
    //    不建 store 后 scheduleDraft/restoreDraft 全是空操作，状态栏也不会出现草稿提示。见 docs/EMBED.md。
    if (!isEmbedSession(window.location.search)) {
      draftStore = createDraftStore(createIdbStorage() ?? createMemoryStorage());
      void draftStore.load().then((draft) => {
        draftInfo.value = draft ? { savedAt: draft.savedAt, name: draft.name } : null;
      });
      const onHide = (): void => {
        void draftStore?.flush();
      };
      window.addEventListener('pagehide', onHide);
      detachDraft = () => window.removeEventListener('pagehide', onHide);
    }
  },

  unmount() {
    void draftStore?.flush();
    detachDraft?.();
    detachDraft = null;
    detachDraw?.();
    detachDraw = null;
    textEdit?.detach();
    textEdit = null;
    detachTransform?.();
    detachTransform = null;
    detachPointer?.();
    detachPointer = null;
    overlay?.destroy();
    overlay = null;
    handle?.destroy();
    handle = null;
  },

  loadText(text: string): boolean {
    resetPaletteDocState(); // 换文档 → 旧的源色映射/高亮不再适用
    resetShapeDocState(); // 同理：形状工作台「哪一组改成了什么」也只对上一份文档成立
    picked.clear();
    if (svg.value) clearSpotlight(svg.value);
    try {
      const parsed = parseSvgText(text);
      header = parsed.header;
      rawSource = text;
      history = new History(parsed.svg);
      svg.value = parsed.svg;
      // 位置锁：每次装载都回到「文档原有图元不可改位置」的受保护态；
      // 「本次新建」的标记随文档作废（撤销栈也是新的，没有跨文档的旧元素）
      freshElements = new WeakSet<Element>();
      ui.geometryLocked = true;
      ui.canvasLock = null; // 画布比例锁同理：只对上一份文档成立，换文档一律回「无限制」
      ui.groupContext = null;
      if (handle) handle.svg = parsed.svg;
      setSelection([], 'replace');
      bump();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resetView());
      return true;
    } catch {
      return false;
    }
  },

  async openFile(file: File): Promise<boolean> {
    const text = await file.text();
    const ok = api.loadText(text);
    if (ok) sourceName.value = file.name;
    return ok;
  },

  execute(cmd) {
    if (!history) return false;
    const applied = history.execute(cmd);
    if (applied) {
      ui.status = cmd.label; // 命令 label 直达状态栏（"画布 1200×856"这类操作看得见反馈）
      bump();
      scheduleDraft();
    }
    return applied;
  },

  undo() {
    if (!history?.undo()) return;
    // 撤销可能重建被删/被解组的节点：剔除已游离的引用，并放弃失效的分组上下文
    if (ui.groupContext && !ui.groupContext.isConnected) ui.groupContext = null;
    selection.value = selection.value.filter((el) => el.isConnected);
    bump();
    syncOverlay();
  },

  redo() {
    if (!history?.redo()) return;
    if (ui.groupContext && !ui.groupContext.isConnected) ui.groupContext = null;
    selection.value = selection.value.filter((el) => el.isConnected);
    bump();
    syncOverlay();
  },

  select: setSelection,
  clearSelection: () => setSelection([], 'replace'),

  exportText: () =>
    svg.value
      ? svgTextForExport({ svg: svg.value, header, rawSource, pristine: pristine.value })
      : '',

  download(filename = sourceName.value ?? 'edited.svg') {
    const text = api.exportText();
    if (text) downloadFile(svgBlob(text), filename.replace(/\.svg$/i, '') + '.svg');
  },

  async exportRaster(options) {
    const root = svg.value;
    if (!root) return { ok: false, message: '没有可导出的文档' };
    const box = viewBoxRect(root);
    if (!box) return { ok: false, message: '读不到画布尺寸（缺少 viewBox）' };
    if (ui.busy) return { ok: false, message: '正在导出，请稍候' };
    ui.busy = true;
    try {
      const { blob, plan } = await rasterizeSvg(api.exportText(), {
        ...options,
        width: box.w,
        height: box.h,
      });
      downloadFile(blob, rasterFileName(sourceName.value, plan, options.format));
      const size = `${plan.pixelWidth}×${plan.pixelHeight} px`;
      const message = plan.clamped
        ? `导出 ${options.format.toUpperCase()}：${size}（超出浏览器上限 ${MAX_RASTER_PX}px，已等比缩小）`
        : `导出 ${options.format.toUpperCase()}：${size}`;
      ui.status = message;
      return { ok: true, message };
    } catch (err) {
      const message = err instanceof Error ? err.message : '导出失败';
      ui.status = message; // 状态栏是提示的唯一出口
      return { ok: false, message };
    } finally {
      ui.busy = false;
    }
  },

  async exportPdf(options = {}) {
    const root = svg.value;
    if (!root) return { ok: false, message: '没有可导出的文档' };
    const content = viewBoxRect(root);
    if (!content) return { ok: false, message: '读不到画布尺寸（缺少 viewBox）' };
    if (ui.busy) return { ok: false, message: '正在导出，请稍候' };
    ui.busy = true;
    try {
      const { blob, filename, box: page } = await buildPdf({
        svgText: api.exportText(),
        width: content.w,
        height: content.h,
        page: options.page,
        sourceName: sourceName.value,
      });
      downloadFile(blob, filename);
      const size = `${Math.round(page.width)}×${Math.round(page.height)} pt`;
      const message = `导出 PDF：${filename}（${size}，矢量）`;
      ui.status = message;
      return { ok: true, message };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'PDF 导出失败';
      ui.status = message; // 状态栏是提示的唯一出口
      return { ok: false, message };
    } finally {
      ui.busy = false;
    }
  },

  relayout() {
    handle?.relayout();
    if (!viewPinned) centerAt(ui.zoom); // "始终居中"：保持当前倍率重新居中
    syncOverlay();
  },

  setZoom(zoom: number) {
    const next = clampZoom(zoom);
    const rect = handle?.host.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) {
      applyView({ zoom: next }); // 没有布局信息（jsdom 等）→ 只改倍率
      return;
    }
    // ★ 以视口中心为锚点缩放：屏幕中心对应的文档点保持不动（否则放大时画板往右下角跑）
    const k = next / ui.zoom;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    applyView({ zoom: next, panX: cx - (cx - ui.panX) * k, panY: cy - (cy - ui.panY) * k });
  },
  panBy,
  zoomFit,

  zoomToSelection() {
    const root = svg.value;
    const host = handle?.host;
    if (!root || !host) return;
    const box = selectionBox() ?? bboxOfMany(list.value.items.map((i) => i.element), root) ?? viewBoxRect(root);
    if (!box) return;
    applyView(fitView({ width: host.clientWidth, height: host.clientHeight }, box, 24));
  },

  syncOverlay,

  // ---------------------------------------------------------------- M1
  deleteSelection() {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    return applyStructure(deleteElements(selection.value, root));
  },

  selectAll() {
    const root = svg.value;
    if (!root) return;
    const els: Element[] = [];
    if (ui.groupContext) {
      for (const child of Array.from(ui.groupContext.children)) if (isEditable(child)) els.push(child);
    } else {
      walkEditable(root, (el) => els.push(el));
    }
    setSelection(els, 'replace');
  },

  groupSelection() {
    const root = svg.value;
    if (!root || selection.value.length < 2) return false;
    // 整组都来自本次新建，新组才算新建 —— 否则动这个组 = 动实验图元（见 allFresh）
    const allOwn = selection.value.every(allFresh);
    const res = groupElements(selection.value, root);
    const ok = applyStructure(res);
    if (ok && allOwn) res.selection.forEach(markFresh);
    return ok;
  },

  ungroupSelection() {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    return applyStructure(ungroupElements(selection.value, root));
  },

  duplicateSelection() {
    const deps = transformDeps();
    if (!deps || selection.value.length === 0) return false;
    const res = duplicateElements(deps, selection.value);
    const ok = applyStructure(res);
    if (ok) res.copies.forEach(markFresh); // 复制出来的同样是新东西
    return ok;
  },

  reorderSelection(mode: OrderMode) {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    return api.execute(reorderElements(selection.value, root, mode));
  },

  alignSelection(mode: AlignMode) {
    const deps = transformDeps();
    const els = [...selection.value];
    if (!deps || els.length < 2) return false;
    if (!els.every(canMove)) return refuseMove(); // 位置锁：对齐会挪位置
    return api.execute(alignCommand(deps, els, boxesOwned(els), mode));
  },

  distributeSelection(mode: DistributeMode) {
    const deps = transformDeps();
    const els = [...selection.value];
    if (!deps || els.length < 3) return false;
    if (!els.every(canMove)) return refuseMove();
    return api.execute(distributeCommand(deps, els, boxesOwned(els), mode));
  },

  nudgeSelection(dx: number, dy: number) {
    const deps = transformDeps();
    if (!deps || selection.value.length === 0) return false;
    if (!selection.value.every(canMove)) return refuseMove(); // 方向键微移也是改位置
    return api.execute(nudgeBy(deps, selection.value, dx, dy));
  },

  setSelectionGeometry(target: GeometryTarget) {
    const deps = transformDeps();
    const box = selectionBox();
    if (!deps || !box) return false;
    if (!selection.value.every(canMove)) return refuseMove(); // X/Y/宽/高
    return api.execute(setGeometry(deps, selection.value, box, target));
  },

  setSelectionRotation(deg: number) {
    const deps = transformDeps();
    const box = selectionBox();
    const current = props.value;
    if (!deps || !box || !current || current.count === 0) return false;
    if (!selection.value.every(canMove)) return refuseMove();
    return api.execute(setRotation(deps, selection.value, box, current.rotation, deg));
  },

  setSelectionRadius(radius: number) {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    return api.execute(setCornerRadius(root, selection.value, radius));
  },

  copySelection() {
    clipboardCount.value = clipboard.copy(selection.value);
    return clipboardCount.value;
  },

  cutSelection() {
    const deps = transformDeps();
    if (!deps || selection.value.length === 0) return false;
    clipboardCount.value = selection.value.length;
    const res = applyStructure({ cmd: clipboard.cut(deps, selection.value), selection: [] });
    return res;
  },

  pasteClipboard() {
    const deps = transformDeps();
    const root = svg.value;
    if (!deps || !root) return false;
    const res = clipboard.paste(deps, ui.groupContext ?? root, 12, 12);
    const ok = api.execute(res.cmd);
    if (ok) {
      res.selection.forEach(markFresh); // 粘出来的是新东西 → 位置锁不拦（整棵子树，组里也一样）
      setSelection(res.selection, 'replace');
    }
    return ok;
  },

  canPaste: computed(() => {
    void clipboardCount.value;
    return clipboard.size() > 0;
  }),

  textProps: computed(() => {
    void version.value;
    void selection.value;
    const el = firstTextOf(selection.value);
    return el ? readTextProps(el) : null;
  }),

  textEditing,

  beginTextEdit() {
    const el = firstTextOf(selection.value);
    return el ? (textEdit?.begin(el) ?? false) : false;
  },

  setTextContent(value, opts) {
    const root = svg.value;
    const el = firstTextOf(selection.value);
    if (!root || !el) return false;
    return api.execute(setTextLines(root, el, value.split('\n'), opts?.mergeKey));
  },

  setTextStyle(changes: TextAttrChanges) {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    return api.execute(setTextAttrs(root, selection.value, changes));
  },

  toggleVertical() {
    const root = svg.value;
    const el = firstTextOf(selection.value);
    if (!root || !el) return false;
    return api.execute(setVertical(root, [el], !readTextProps(el).vertical));
  },

  setHidden(hidden: boolean, els?: Element[]) {
    const root = svg.value;
    const targets = els ?? selection.value;
    if (!root || targets.length === 0) return false;
    return api.execute(setHidden(root, targets, hidden));
  },

  toggleHidden(els?: Element[]) {
    const targets = els ?? selection.value;
    const first = targets[0];
    if (!first) return false;
    return api.setHidden(first.getAttribute('display') !== 'none', targets);
  },

  // ---------------------------------------------------------------- M3
  // ★ 必须显式读一次 version：否则连续第二次改动不会让源码文本失效（滞后一版）。
  // pristine 只在"有/无改动"翻转时才通知，同值重算不会触发外层 computed —— 真踩过。
  sourceText: computed(() => {
    void version.value;
    return api.exportText();
  }),

  applySource(text) {
    const root = svg.value;
    if (!root) return false;
    if (text.trim() === api.sourceText.value.trim()) return false; // 没改就别动
    const cmd = sourceReplaceCommand(root, text, header, {
      onHeader: (next) => {
        header = next;
      },
    });
    if (!cmd) {
      ui.status = '源码解析失败：请检查标签是否闭合';
      return false;
    }
    const ok = api.execute(cmd);
    if (ok) {
      if (ui.groupContext && !ui.groupContext.isConnected) ui.groupContext = null;
      selection.value = selection.value.filter((el) => el.isConnected);
      syncOverlay();
    }
    return ok;
  },

  newDocument() {
    if (!api.loadText(EMPTY_DOC)) return;
    sourceName.value = null;
    ui.status = '新建空文档';
  },

  openText(text, name = null) {
    if (!looksLikeSvgDocument(text)) {
      ui.status = '这不是一个 SVG 文档（内容不像 SVG）';
      return false;
    }
    if (!api.loadText(text)) {
      ui.status = 'SVG 解析失败';
      return false;
    }
    sourceName.value = name;
    ui.status = `已打开 ${name ?? '粘贴的 SVG'}`;
    return true;
  },

  exportProject() {
    const root = svg.value;
    if (!root) return;
    const project = buildProject(api.exportText(), {
      name: sourceName.value ?? '未命名',
      view: { zoom: ui.zoom, panX: ui.panX, panY: ui.panY },
      collapsed: ui.collapsed,
      locked: collectLockedPaths(root),
      selection: selection.value
        .map((el) => pathOfElement(root, el))
        .filter((p): p is number[] => p !== null),
      context: ui.groupContext ? pathOfElement(root, ui.groupContext) : null,
    });
    const name = projectFilename(project.name);
    downloadFile(new Blob([projectToText(project)], { type: 'application/json' }), name);
    ui.status = `已导出工程 ${name}`;
  },

  importProjectText(text) {
    let project;
    try {
      project = parseProject(text);
    } catch (error) {
      ui.status = error instanceof ProjectError ? `工程文件打不开：${error.message}` : '工程文件打不开';
      return false;
    }
    if (!api.loadText(project.svg)) {
      ui.status = '工程文件里的 SVG 解析失败';
      return false;
    }
    const root = svg.value;
    if (!root) return false;

    applyView(project.view);
    ui.collapsed = { ...project.collapsed };
    // 锁定是编辑器私有状态：直接写回，不产生命令（导入后文档仍是"未修改"）
    restoreLockedState(root, project.locked);
    ui.groupContext = project.context ? (resolvePaths(root, [project.context])[0] ?? null) : null;
    selection.value = resolvePaths(root, project.selection);
    ui.status = `已导入工程 ${project.name}`;
    bump();
    syncOverlay();
    return true;
  },

  async openProjectFile(file) {
    return api.importProjectText(await file.text());
  },

  canvasInfo: computed(() => {
    void version.value;
    return svg.value ? readCanvas(svg.value) : null;
  }),

  docBackground: computed(() => {
    void version.value;
    return svg.value ? canvasBackgroundOf(svg.value) : null;
  }),

  setCanvasSize(size) {
    const root = svg.value;
    return root ? api.execute(setCanvasSize(root, size)) : false;
  },

  setCanvasRatio(ratio) {
    const root = svg.value;
    return root ? api.execute(setCanvasRatio(root, ratio)) : false;
  },

  setCanvasBackground(color, mode) {
    if (mode === 'view') {
      ui.canvasBg = color; // 只影响编辑器视图
      return true;
    }
    const root = svg.value;
    return root ? api.execute(setCanvasBackground(root, color)) : false;
  },

  lockSelection(locked) {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    const ok = api.execute(setLocked(root, selection.value, locked));
    if (ok) syncOverlay();
    return ok;
  },

  lockElement(el, locked) {
    const root = svg.value;
    if (!root) return false;
    const ok = api.execute(setLocked(root, [el], locked));
    if (ok) syncOverlay();
    return ok;
  },

  geometryLocked: computed(() => ui.geometryLocked),

  canMoveSelection: computed(() => {
    void version.value;
    void selection.value;
    return selectionMovable();
  }),

  setGeometryLocked(locked: boolean) {
    if (ui.geometryLocked === locked) return;
    ui.geometryLocked = locked;
    ui.status = locked ? '位置已锁定 · 文档原有图元的位置不可改' : '位置可改 · 拖动 / 手柄 / 坐标输入已生效';
    syncOverlay(); // 手柄随之出现或消失
  },

  renameSelectionId(id) {
    const root = svg.value;
    if (!root || selection.value.length === 0) return false;
    return api.execute(renameId(root, selection.value, id));
  },

  renameElementId(el, id) {
    const root = svg.value;
    if (!root) return false;
    return api.execute(renameId(root, [el], id));
  },

  colorUsages: computed(() => {
    void version.value;
    const root = svg.value;
    return root ? collectColorUsage(root) : [];
  }),

  previewColor(hex) {
    previewHex = hex; // 悬停：只做临时预览（画框 + 其余压暗），选中集原封不动
    if (hex !== null) previewShapeEls = null;
    applyHighlight();
  },

  shapeGroups: computed(() => {
    void version.value;
    const root = svg.value;
    return root ? buildGroups(root, shapeState.axis, shapeState.scope) : [];
  }),

  previewShapeGroup(key) {
    previewShapeEls = key === null ? null : [...(api.shapeGroups.value.find((g) => g.key === key)?.elements ?? [])];
    previewHex = null; // 与色板预览互斥：同一时刻只高亮一件事
    applyHighlight();
  },

  selectShapeGroup(key) {
    const group = api.shapeGroups.value.find((g) => g.key === key);
    if (!group) return false;
    previewShapeEls = null;
    applyHighlight();
    setSelection([...group.elements], 'replace');
    ui.status = `已选 ${group.count} 个元素 · ${group.label}`;
    return true;
  },

  applyShapeGroup(key, kind) {
    const root = svg.value;
    const group = api.shapeGroups.value.find((g) => g.key === key);
    if (!root || !group) {
      setShapeHint('这一组已经不在文档里了（画布变过？悬停一下刷新）');
      return false;
    }
    const out = replaceShapes(root, group.elements, kind, { fit: shapeState.fit, onSwap: shapeSwapHandoff });
    if (!out) {
      setShapeHint(`${group.label}：这一组已经是${SHAPE_LABEL[kind]}了`);
      ui.status = '没有可改动的元素（已经是这个形状）';
      return false;
    }
    if (!api.execute(out.cmd)) {
      setShapeHint('这次改动被拒绝了（元素在预览后被改过），没有写入');
      return false;
    }
    markShapeApplied(key, kind);
    const skipped = out.skipped.length > 0 ? `，跳过 ${out.skipped.length} 个` : '';
    setShapeHint(`${group.label} · ${out.cmd.label}${skipped}（Ctrl+Z 可撤回）`);
    ui.status = out.cmd.label;
    // 换完之后元素身份变了：把预览跟到这一组**新**的元素上，浮层开着时还能继续对比
    const fresh = api.shapeGroups.value.find((g) => g.key === key);
    previewShapeEls = fresh ? [...fresh.elements] : null;
    applyHighlight();
    return true;
  },

  /**
   * 属性面板「形状」区：把**当前选中集**换成同一种形状。
   *
   * 与形状工作台走同一个 op（`replaceShapes`），差别只在元素从哪来、提示写给谁：
   * 「这一组」是批量语义，这里是「选中的这几个」（一个元素只是长度为 1 的特例）。
   * 所以这次**没有** `shapeState.applied` 记账 —— 那是给「哪一组改成了什么」用的，
   * 单选时面板直接按几何反推当前形状（core/query/shape-kind），不需要记。
   */
  applySelectionShape(kind) {
    const root = svg.value;
    const targets = selection.value.filter((el) => isConvertibleTag(el.tagName.toLowerCase()));
    if (!root || targets.length === 0) {
      ui.status = '选中的元素没有几何形状（文字 / 图片 / 组换不了）';
      return false;
    }
    const out = replaceShapes(root, targets, kind, { fit: shapeState.fit, onSwap: shapeSwapHandoff });
    if (!out) {
      ui.status = `选中的元素已经是${SHAPE_LABEL[kind]}了`;
      return false;
    }
    if (!api.execute(out.cmd)) return false;
    const skipped = out.skipped.length > 0 ? `，跳过 ${out.skipped.length} 个` : '';
    ui.status = `${out.cmd.label}${skipped}（Ctrl+Z 可撤回）`;
    return true;
  },

  applyColorMapping(opts = {}) {
    const root = svg.value;
    if (!root) return false;
    const usages = api.colorUsages.value;
    if (usages.length === 0) {
      ui.status = '文档里没有可映射的颜色';
      return false;
    }
    const keep = opts.keepLuminance ?? palette.keepLuminance;
    const only = opts.selectionOnly ? new Set(selection.value) : undefined;
    const cmd = applyColorMappingCmd(root, usages, palette.mapping, { keepLuminance: keep, only });
    if (cmd.patches.length === 0) {
      ui.status = only ? '选中的元素里没有命中映射的颜色' : '映射为空（或全部映射到自身）';
      return false;
    }
    // 先记账（用"应用前"的命中集）：之后单行改色要按元素定位，不能靠源色再匹配
    const written = resolveMapping(usages, palette.mapping, { keepLuminance: keep });
    for (const usage of usages) {
      const to = written.get(usage.hex);
      if (!to) continue;
      picked.set(usage.hex, {
        els: usage.elements,
        rgba: usage.rgba,
        hex: to,
        origins: paintOrigins(root, usage.elements, usage.hex),
      });
    }
    const ok = api.execute(cmd);
    if (ok) ui.status = cmd.label;
    return ok;
  },

  revertColorPick(source) {
    const rec = picked.get(source);
    if (!rec) return false;
    picked.delete(source);
    const cmd = revertPickCommand(rec);
    if (!cmd) return false;
    const ok = api.execute(cmd);
    if (ok) ui.status = cmd.label;
    return ok;
  },

  pickColorMapping(source, target, opts = {}) {
    const root = svg.value;
    if (!root) return false;
    const keep = palette.keepLuminance;
    const prev = picked.get(source);
    let cmd: Command | null = null;
    let written: string;

    if (prev && prev.els.length > 0) {
      // 改写过的行：按元素改（源色/上次写入色都算命中，所以撤销后再选也不会"点了没反应"）
      cmd = recolorElements(root, prev.els, [source, prev.hex], target);
      written = resolveOne({ hex: source, rgba: prev.rgba }, target, { keepLuminance: keep }) ?? target;
    } else {
      const usages = api.colorUsages.value;
      const hit = usages.find((u) => u.hex === source);
      if (!hit) {
        ui.status = `${source} 已不在文档里`;
        return false;
      }
      cmd = applyColorMappingCmd(root, usages, { [source]: target }, { keepLuminance: keep });
      written = resolveOne(hit, target, { keepLuminance: keep }) ?? target;
      picked.set(source, {
        els: hit.elements,
        rgba: hit.rgba,
        hex: written,
        origins: paintOrigins(root, hit.elements, source),
      });
    }

    if (!cmd || cmd.patches.length === 0) {
      ui.status = '没有可改写的颜色';
      return false;
    }
    const ok = api.execute(opts.merge ? { ...cmd, mergeKey: `pal-pick:${source}` } : cmd);
    if (ok) {
      if (prev) picked.set(source, { ...prev, hex: written });
      ui.status = cmd.label;
    }
    return ok;
  },

  draft: draftInfo,

  async restoreDraft() {
    const draft = await draftStore?.load();
    if (!draft) return false;
    const ok = api.loadText(draft.svg);
    if (!ok) return false;
    sourceName.value = draft.name;
    if (draft.view) applyView(draft.view);
    ui.status = '已恢复上次未保存的改动';
    return true;
  },

  async discardDraft() {
    await draftStore?.clear();
    draftInfo.value = null;
    ui.status = '已丢弃草稿';
  },

  async flushDraft() {
    await draftStore?.flush();
    const latest = await draftStore?.load();
    draftInfo.value = latest ? { savedAt: latest.savedAt, name: latest.name } : null;
  },

  exitGroupContext() {
    const ctx = ui.groupContext;
    const root: Element | null = svg.value;
    if (!ctx || !root) return false;
    const up: Element | null = ctx.parentElement;
    ui.groupContext = up && up !== root && up.tagName.toLowerCase() === 'g' ? up : null;
    setSelection(ui.groupContext ? [ui.groupContext] : [], 'replace');
    return true;
  },
};

export const useEditor = (): EditorApi => api;
